﻿import { fetchRequest } from '@tk/utilities/tk.fetch';
import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import render from '@tk/utilities/tk.render';

export default class TKPopup extends TKCustomElementFactory {
    url?: string;
    button?: HTMLButtonElement;
    popup?: HTMLDialogElement;
    offset: number;

    constructor() {
        super();

        this.url = this.getAttribute('data-tk-url') || undefined;
        this.button = this.querySelector('button') || undefined;
        this.offset = Number(this.getAttribute('data-tk-offset')) || 4;
    }

    connectedCallback(): void {
        if (!this.url) throw new Error('Dialog: URL is missing!');
        this.registerClickListener();
    }

    registerClickListener(): void {
        if (!this.button) return;
        const onClick = this.openDialog.bind(this, this.url!);
        this.pushListener({ event: 'click', element: this.button, action: onClick });
    }

    registerCloseListener(): void {
        const onClick = this.closeDialogIfOutside.bind(this);
        this.pushListener({ event: 'click', element: window, action: onClick });
    }

    createDialog() {
        this.popup = this.render();
        const onClosed = this.closedDialog.bind(this);
        this.pushListener({ event: 'close', element: this.popup, action: onClosed });
    }

    openDialog(url: string) {
        this.getContent(url);
        // This method would be used to execute code in a non-blocking way to prevent potential delays.
        setTimeout(() => {
            this.registerCloseListener();
        });
    }

    getContent(url: string): void {
        fetchRequest({
            requestURL: url,
            resolveHandler: this.handleResponseSuccess.bind(this),
        });
    }

    getContentWithData(url: string, payload: Record<string, string>): void {
        fetchRequest({
            requestURL: url,
            resolveHandler: this.handleResponseSuccess.bind(this),
            payload,
        });
    }

    handleResponseSuccess(response: TKResponse): void {
        if (response && response.success) {
            this.createDialog();
            const content = this.popup?.querySelector('[data-tk-dialog-content]');
            if (!content) return;
            content.innerHTML = response.dataAsHtml;
            this.open();
        }
    }

    open() {
        if (!this.popup) return;
        this.setPosition(this.popup);
        this.popup.show();
    }

    static attachButtonClickHandler(
        dialog: HTMLDialogElement,
        selector: string,
        callback: (button: HTMLButtonElement) => void,
    ): void {
        const buttons = dialog.querySelectorAll<HTMLButtonElement>(selector);
        buttons.forEach((button) => {
            button.addEventListener('click', () => callback(button));
        });
    }

    closedDialog(): void {
        this.removeListener(window);
        this.popup?.remove();
    }

    render() {
        const dialog = render<HTMLDialogElement>(`
            <dialog class="tk-dialog tk-dialog--popup">
                <button
                    type="button"
                    class="tk-button tk-button--tertiary tk-button--only-icon tk-dialog__close"
                    data-tk-dialog-close
                ></button>
                <div class="tk-dialog__inner" data-tk-dialog-content></div>
            </dialog>
        `);
        document.body.insertAdjacentElement('beforeend', dialog);
        TKPopup.attachButtonClickHandler(dialog, '[data-tk-dialog-close]', () => dialog.close());
        return dialog;
    }

    setPosition(element: HTMLElement) {
        const { top, height, left, right } = this.button!.getBoundingClientRect();
        const leftPosition = left + window.scrollX;
        const rightPosition = right + window.scrollX;

        element.style.top = `${top + height + window.scrollY + this.offset}px`;

        if (window.innerWidth <= element.clientWidth + leftPosition) {
            element.style.left = `${rightPosition - element.clientWidth}px`;
        } else {
            element.style.left = `${leftPosition}px`;
        }
    }

    closeDialogIfOutside(event: MouseEvent) {
        /**
         * Checking the coordinates of the client for the value 0 and the
         * HTMLSelectElement or HTMLOptionElement element ensures that the
         * dialogue does not close in Mozilla. This is a bug in Firefox
         * that has not yet been fixed.
         */
        if (!this.popup) return;
        if (
            event.clientX === 0 &&
            event.clientY === 0 &&
            (event.target instanceof HTMLSelectElement || event.target instanceof HTMLOptionElement)
        )
            return;
        const dialogDimensions = this.popup.getBoundingClientRect();
        if (
            event.clientX < dialogDimensions.left ||
            event.clientX > dialogDimensions.right ||
            event.clientY < dialogDimensions.top ||
            event.clientY > dialogDimensions.bottom
        ) {
            this.popup.close();
        }
    }
}
